import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-bold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-black/50 shadow-button-blue hover:shadow-floating",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-button hover:shadow-floating",
        outline:
          "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-flat hover:shadow-raised",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-button hover:shadow-raised",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "bg-gradient-cta text-white hover:opacity-90 shadow-button-blue hover:shadow-floating",
        glass: "bg-background/80 backdrop-blur-xl text-foreground hover:bg-background shadow-raised hover:shadow-floating",
        "primary-cta": "bg-cta-primary text-cta-primary-foreground hover:bg-cta-primary-hover shadow-button-orange hover:shadow-floating font-bold",
      },
      size: {
        default: "min-h-[48px] px-6 py-3",
        sm: "min-h-[40px] rounded-sm px-4",
        lg: "min-h-[56px] rounded-sm px-8 text-lg",
        icon: "min-h-[48px] w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
